export const REGION_HOSTNAMES: Record<string, string> = {
  us: "admin.premiercardgrading.com",
  nz: "admin.premiercardgrading.co.nz",
  au: "admin.premiercardgrading.com.au",
  my: "admin.premiercardgrading.com.my",
  eu: "admin.premiercardgrading.eu",
  uk: "admin.premiercardgrading.co.uk",
  jp: "admin.premiercardgrading.jp",
  ph: "admin.premiercardgrading.ph",
  vn: "admin.premiercardgrading.vn",
  ca: "admin.premiercardgrading.ca",
  th: "admin.premiercardgrading.co.th",
  events: "admin.pcgcardevents.com",
};

export const REGION_ADMIN_URLS: Record<string, string> = Object.entries(REGION_HOSTNAMES).reduce(
  (acc, [region, hostname]) => ({
    ...acc,
    [region]: `https://${hostname}`,
  }),
  {}
);

// The generic admin dashboard has no region in its host. The embedded Shopify
// app sends unmapped/test installs here (with an optional ?shop= hint); real
// staff get routed on to their regional portal by RegionLandingGuard.
export const GENERIC_DASHBOARD_HOSTNAME = "admindashboard.premiercardgrading.com";

// Optional build-time override so the host-gated landing logic can be exercised
// from a local tunnel (e.g. a Cloudflare tunnel) without spoofing the prod host.
// Set REACT_APP_GENERIC_DASHBOARD_HOST before `yarn start`; it is unset in
// production builds, so prod behaviour is unchanged.
const GENERIC_DASHBOARD_HOST_OVERRIDE = process.env.REACT_APP_GENERIC_DASHBOARD_HOST;

export const isGenericDashboardHost = (): boolean => {
  const host = window.location.hostname;
  return (
    host === GENERIC_DASHBOARD_HOSTNAME ||
    (!!GENERIC_DASHBOARD_HOST_OVERRIDE && host === GENERIC_DASHBOARD_HOST_OVERRIDE)
  );
};

// Regions whose admin portal host is reserved in REGION_HOSTNAMES but is not
// live yet. They are NOT auto-routed to — users land on the in-app fallback
// instead of being redirected to a dead host. Remove a region here once its
// portal goes live (vn and th are pending; events is live at admin.pcgcardevents.com).
//
// th: a26984b (2026-09-16) mapped Canada and Thailand together and treated both as live.
// Canada's portal is; admin.premiercardgrading.co.th does not resolve, and Robert confirmed
// on 2026-09-18 that Thailand's is not imminent — so th is delisted, deliberately reversing
// that commit's assertion. Remove it here when the host goes live.
export const REGIONS_WITHOUT_LIVE_PORTAL = new Set<string>(["vn", "th"]);

// A region is routable only if it maps to a real, live regional admin portal.
export const regionHasAdminPortal = (region: string): boolean => {
  const r = (region ?? "").trim().toLowerCase();
  return !!r && !!REGION_ADMIN_URLS[r] && !REGIONS_WITHOUT_LIVE_PORTAL.has(r);
};

// Non-franchise sentinel regions (e.g. unassigned, dev, dev3) are NOT mapped to a dedicated portal
// host and are served IN-PLACE on the generic dashboard, scoped to the user. Distinct from a real
// franchise region whose portal just isn't live yet (e.g. vn, which IS in REGION_HOSTNAMES and
// keeps its "assigned but no portal" guidance instead of a scoped app).
export const regionServesGenericApp = (region: string): boolean => {
  const r = (region ?? "").trim().toLowerCase();
  return !!r && !REGION_HOSTNAMES[r];
};

// RegionLandingGuard pins the scoped region here (sessionStorage; cleared on non-scoped landings)
// so module-load derivations (getBaseUrl, i18n) recompute against it after a one-time reload.
export const SCOPED_REGION_STORAGE_KEY = "pcg.scopedRegion";

export const getScopedRegionOverride = (): string | null => {
  try {
    return window.sessionStorage.getItem(SCOPED_REGION_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setScopedRegionOverride = (region: string): void => {
  try {
    window.sessionStorage.setItem(SCOPED_REGION_STORAGE_KEY, region);
  } catch {
    /* sessionStorage unavailable (e.g. private mode) — app falls back to the dev scope */
  }
};

export const clearScopedRegionOverride = (): void => {
  try {
    window.sessionStorage.removeItem(SCOPED_REGION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const getCurrentRegion = (): string | null => {
  const { hostname } = window.location;

  const region = Object.entries(REGION_HOSTNAMES).find(([_, h]) => h === hostname)?.[0];

  if (region) return region;

  // Generic dashboard host: dev/test/unlinked accounts are served in-place, scoped to their own
  // region (pinned by RegionLandingGuard). Use it so the per-region API base + i18n resolve to it.
  if (isGenericDashboardHost()) {
    const scoped = getScopedRegionOverride();
    if (scoped) return scoped;
  }

  // For development
  return "dev";
};

export const getRegionAdminUrl = (region: string, fallbackUrl?: string): string => {
  const r = region.toLowerCase();
  if (REGION_ADMIN_URLS[r]) return REGION_ADMIN_URLS[r];
  return fallbackUrl || `https://admin.premiercardgrading.${r}`;
};

export const isHostnameForRegion = (region: string): boolean => {
  return getCurrentRegion() === region;
};
